import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Stack, HStack } from '@chakra-ui/react';
import { AddIcon, EditIcon, CopyIcon } from '@chakra-ui/icons';

import Button from 'components/v4/Button';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import ActionsButton from 'components/tables/AGGrid/ActionsButton';
import { useGetDialPlans, useCopyDialPlan } from './queries';
import { headingStyling } from 'components/forms/new/formStyles';
import CopyDialPlanModal from './components/CopyDialPlanModal';

const DialPlansTable = () => {
    const navigate = useNavigate();
    const gridRef = useRef();
    const [copyModalData, setCopyModalData] = useState(null);

    const {
        data: dialPlans,
        isLoading,
        isFetching,
        refetch,
    } = useGetDialPlans();
    const { mutate: copyDialPlan, isLoading: isCopying } = useCopyDialPlan();

    const columnDefs = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                sort: 'asc',
            },
            {
                headerName: 'Action',
                field: 'action',
                maxWidth: 100,
                cellRenderer: (params) => {
                    const actions = [
                        {
                            label: 'Edit',
                            icon: EditIcon,
                            onClick: () =>
                                navigate(`/dial-plans/${params.data.id}`),
                        },
                        {
                            label: 'Copy',
                            icon: CopyIcon,
                            onClick: () => setCopyModalData(params.data),
                        },
                    ];
                    return <ActionsButton actions={actions} />;
                },
                filter: false,
                sortable: false,
            },
        ],
        [navigate],
    );

    const tableDataWithColumns = useMemo(
        () => ({
            columns: columnDefs,
            data: dialPlans || [],
        }),
        [dialPlans, columnDefs],
    );

    const handleAdd = () => {
        navigate('/dial-plans/add');
    };

    const handleCopy = (copyName) => {
        copyDialPlan(
            { id: copyModalData.id, copyName },
            {
                onSuccess: () => {
                    setCopyModalData(null);
                },
            },
        );
    };

    return (
        <Stack spacing={4}>
            <Heading sx={headingStyling}>Dial Plans</Heading>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isLoading}
                isFetching={isFetching}
                titleName="Dial Plans"
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            size="sm"
                            onClick={handleAdd}>
                            Dial Plans
                        </Button>
                    </HStack>
                }
                refetch={refetch}
            />
            {copyModalData && (
                <CopyDialPlanModal
                    isOpen={!!copyModalData}
                    onClose={() => setCopyModalData(null)}
                    onCopy={handleCopy}
                    planName={copyModalData.name}
                    isLoading={isCopying}
                />
            )}
        </Stack>
    );
};

export default DialPlansTable;
